import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';

import type { InlineBasisMode, ModalBasisMode } from '../calculator/tpsl-basis';

/**
 * Persistence entrances for TP/SL basis modes.
 * Order Form Inline + Advanced share `order-form`.
 * Keys are entrance-only (no Long/Short, no symbol).
 */
export type TpslBasisEntrance = 'order-form' | 'order-tpsl' | 'position-tpsl-entire' | 'position-tpsl-partial';

/** Shared Order Form state: `price` = Price_or_change_pct (Inline Price ↔ Advanced Change%). */
export type OrderFormInlineBasisModes = {
  tp: InlineBasisMode;
  sl: InlineBasisMode;
};

/** `limit` is always `change` at read/persist time (Change(%) only on Limit row). */
export type ModalLegBasisModes = {
  trigger: ModalBasisMode;
  limit: ModalBasisMode;
};

export type ModalEntranceBasisModes = {
  tp?: ModalLegBasisModes;
  sl?: ModalLegBasisModes;
  /** position/order single-leg modals */
  trigger?: ModalBasisMode;
  limit?: ModalBasisMode;
};

export const defaultOrderFormInlineBasisModes: OrderFormInlineBasisModes = {
  tp: 'price',
  sl: 'price',
};

/**
 * Inline (Variant A) secondary reset after mode switch / hydrate.
 * Empty string so mode placeholders show: Enter price / Enter P&L / Enter ROI.
 * (Modal Variant B empty secondary uses sign placeholder `''` / `-`.)
 */
export const inlineSecondaryResetValue = '';

function isInlineBasisMode(value: unknown): value is InlineBasisMode {
  return value === 'price' || value === 'roi' || value === 'pnl';
}

/** Validate persisted payload; invalid/missing → null (caller uses defaults). */
export function parseOrderFormInlineBasisModes(raw: unknown): OrderFormInlineBasisModes | null {
  if (raw == null || typeof raw !== 'object') return null;
  const { tp, sl } = raw as { tp?: unknown; sl?: unknown };
  if (!isInlineBasisMode(tp) || !isInlineBasisMode(sl)) return null;
  return { tp, sl };
}

/** Key = entrance only (no Long/Short). */
function storageKey(entrance: TpslBasisEntrance): `flipster.configs.v1.tpslBasis.${string}` {
  return `flipster.configs.v1.tpslBasis.${entrance}`;
}

/** missing key → null; caller applies surface defaults */
export function readBasisModes(entrance: TpslBasisEntrance): unknown | null {
  return getLocalStorage(storageKey(entrance));
}

/** Persist basis modes for an entrance. */
export function writeBasisModes(entrance: TpslBasisEntrance, payload: unknown): void {
  setLocalStorage(storageKey(entrance), payload);
}

/** Inline `price` ↔ Advanced `change` (shared Price_or_change_pct). */
export function inlineModeToModalMode(mode: InlineBasisMode): ModalBasisMode {
  return mode === 'price' ? 'change' : mode;
}

export function modalModeToInlineMode(mode: ModalBasisMode): InlineBasisMode {
  return mode === 'change' ? 'price' : mode;
}

/**
 * Seed Advanced modes from shared Order Form state.
 * Trigger follows mapped Inline mode; Limit is always Change(%) (no ROI/PnL on Limit row).
 */
export function orderFormModesToAdvanced(modes: OrderFormInlineBasisModes): AdvancedOrderTpslBasisModes {
  const tpMode = inlineModeToModalMode(modes.tp);
  const slMode = inlineModeToModalMode(modes.sl);
  return {
    tp: { trigger: tpMode, limit: 'change' },
    sl: { trigger: slMode, limit: 'change' },
  };
}

/** Map Advanced → shared Order Form (trigger mode is SoT per leg). */
export function advancedModesToOrderForm(modes: AdvancedOrderTpslBasisModes): OrderFormInlineBasisModes {
  return {
    tp: modalModeToInlineMode(modes.tp.trigger),
    sl: modalModeToInlineMode(modes.sl.trigger),
  };
}

/** Read shared Order Form modes. */
export function readOrderFormInlineBasisModes(): OrderFormInlineBasisModes | null {
  return parseOrderFormInlineBasisModes(readBasisModes('order-form'));
}

/**
 * Persist shared Order Form TP/SL basis modes.
 * Inline: call on selection change. Advanced: call on Confirm only.
 */
export function persistOrderFormInlineBasisModes(modes: OrderFormInlineBasisModes): void {
  writeBasisModes('order-form', modes);
}

export const defaultModalLegBasisModes: ModalLegBasisModes = {
  trigger: 'change',
  limit: 'change',
};

function isModalBasisMode(value: unknown): value is ModalBasisMode {
  return value === 'change' || value === 'roi' || value === 'pnl';
}

/**
 * Validate persisted payload; invalid/missing → null (caller uses `change` defaults).
 * Limit is always coerced to Change(%) — ROI/PnL apply to Trigger only.
 */
export function parseModalEntranceBasisModes(raw: unknown): ModalLegBasisModes | null {
  if (raw == null || typeof raw !== 'object') return null;
  const { trigger, limit } = raw as { trigger?: unknown; limit?: unknown };
  if (!isModalBasisMode(trigger) || !isModalBasisMode(limit)) return null;
  return { trigger, limit: 'change' };
}

/**
 * Resolve persistence entrance for Position / Pending TPSL modal (Variant B).
 * pending → order-tpsl; open entire/partial → position-tpsl-*.
 */
export function resolveTpslModalBasisEntrance(
  positionType: 'open' | 'pending',
  tpslTab: 'entire' | 'partial',
): Exclude<TpslBasisEntrance, 'order-form'> {
  if (positionType === 'pending') return 'order-tpsl';
  return tpslTab === 'partial' ? 'position-tpsl-partial' : 'position-tpsl-entire';
}

export function readModalEntranceBasisModes(entrance: TpslBasisEntrance): ModalLegBasisModes | null {
  return parseModalEntranceBasisModes(readBasisModes(entrance));
}

/** Persist modal trigger/limit basis modes — call only after successful Confirm. */
export function persistModalEntranceBasisModes(entrance: TpslBasisEntrance, modes: ModalLegBasisModes): void {
  writeBasisModes(entrance, { trigger: modes.trigger, limit: 'change' });
}

/** Advanced Order TP/SL — dual-leg (tp + sl) each with trigger/limit modes. */
export type AdvancedOrderTpslBasisModes = {
  tp: ModalLegBasisModes;
  sl: ModalLegBasisModes;
};

export const defaultAdvancedOrderTpslBasisModes: AdvancedOrderTpslBasisModes = {
  tp: defaultModalLegBasisModes,
  sl: defaultModalLegBasisModes,
};

/** Validate Advanced dual-leg payload; invalid/missing → null. */
export function parseAdvancedOrderTpslBasisModes(raw: unknown): AdvancedOrderTpslBasisModes | null {
  if (raw == null || typeof raw !== 'object') return null;
  const { tp, sl } = raw as { tp?: unknown; sl?: unknown };
  const parsedTp = parseModalEntranceBasisModes(tp);
  const parsedSl = parseModalEntranceBasisModes(sl);
  if (!parsedTp || !parsedSl) return null;
  return { tp: parsedTp, sl: parsedSl };
}

/** Read Advanced modes from unified Order Form (mapped). */
export function readAdvancedOrderTpslBasisModes(): AdvancedOrderTpslBasisModes | null {
  const orderForm = readOrderFormInlineBasisModes();
  return orderForm ? orderFormModesToAdvanced(orderForm) : null;
}

/**
 * Persist Advanced tp/sl basis modes into unified Order Form (Confirm only).
 * Maps trigger modes → Inline Price/ROI/PnL shared state.
 */
export function persistAdvancedOrderTpslBasisModes(modes: AdvancedOrderTpslBasisModes): void {
  persistOrderFormInlineBasisModes(advancedModesToOrderForm(modes));
}

/** Position TPSL first-time tooltip (not Pending / Advanced / Inline). */
const positionFirstTimeTooltipKey = 'flipster.configs.v1.tpslBasis.positionFirstTimeTooltipSeen';

export function hasSeenPositionBasisFirstTimeTooltip(): boolean {
  return getLocalStorage(positionFirstTimeTooltipKey) === true;
}

export function markPositionBasisFirstTimeTooltipSeen(): void {
  setLocalStorage(positionFirstTimeTooltipKey, true);
}
